import { logger } from '@/monitoring/logger'
import {
  BroadcastEventType,
  ConnectionStatus,
} from '@/resources/enums/websocket'
import { getAccessToken } from '@/resources/services/permission/auth'
import {
  ChannelMessage,
  ConnectionStatusCallback,
  Interval,
  MessageHandlerCallback,
  Timeout,
} from '@/resources/types/websocket'
import { isMswEnvironment } from '@/resources/utils'
import {
  BROADCAST_CHANNEL,
  broadcastOrders,
  clearAllTimers,
  closeWebSocket,
  getFollowerTabTimeout,
  getLeaderIsAliveInterval,
  getLeaderTabFromStorage,
  getRetryTimeout,
  getTabId,
  getWebSocketUrl,
  MAX_RETRIES,
  removeLeaderTabFromStorage,
  removeTimeout,
  requestConnectionState,
  sendConnectionStatus,
  updateLeaderTabOnStorage,
  WEBSOCKET_LEADER_KEY,
} from '@/resources/utils/websocket'

const webSocketUrl = getWebSocketUrl()
const tabId = getTabId()
let jwtToken = getAccessToken()

let isEnabled = false

let websocket: WebSocket | null = null
let channel: BroadcastChannel | null = null
let leaderInterval: Interval = null
let leaderTimeout: Timeout = null
let isLeader = false
let retryCount = 0
let retryTimeout: Timeout = null

let currentConnectionStatus: ConnectionStatus = ConnectionStatus.NOT_STARTED
let hasRequestedState = false

let onMessageHandler: MessageHandlerCallback = () => null
let onConnectionStatusChange: ConnectionStatusCallback = () => null

export const setIsEnabled = (enabled: boolean) => {
  jwtToken = getAccessToken()
  isEnabled = enabled && !!webSocketUrl && !!jwtToken && !isMswEnvironment
  if (!isEnabled) {
    if (isLeader) {
      removeLeaderTabFromStorage()
      removeOwnLeadership()
    }
    clearAllTimers(leaderInterval, leaderTimeout, retryTimeout)
    channel?.close()
  }
}

export const resetValues = () => {
  isEnabled = false
  isLeader = false
  retryCount = 0
  currentConnectionStatus = ConnectionStatus.NOT_STARTED
  hasRequestedState = false
  websocket = null
  channel = null
  clearAllTimers(leaderInterval, leaderTimeout, retryTimeout)
}

const setMessageHandler = (handler: MessageHandlerCallback) => {
  onMessageHandler = handler
}

const setConnectionStatusHandler = (handler: ConnectionStatusCallback) => {
  onConnectionStatusChange = handler
}

const notifyConnectionStatus = (status: ConnectionStatus) => {
  if (!channel || !isLeader) return
  currentConnectionStatus = status
  sendConnectionStatus(channel, status, tabId)
  onConnectionStatusChange(status)
}

const getChannelOnMessageCallback = (event: MessageEvent<ChannelMessage>) => {
  if (event.data.tabId === tabId) return

  switch (event.data.type) {
    case BroadcastEventType.CONNECTION_STATUS:
      currentConnectionStatus = event.data.status
      onConnectionStatusChange(event.data.status)
      break
    case BroadcastEventType.MESSAGE:
      onMessageHandler(event.data.payload)
      break
    case BroadcastEventType.REQUEST_STATE:
      notifyConnectionStatus(currentConnectionStatus)
      break
    case BroadcastEventType.LEADER_IS_ALIVE:
      if (!isLeader) {
        startFollowerTimeout()
      }
      break
    default:
      break
  }
}

const initBroadcastChannel = () => {
  if (!isEnabled) return
  channel = new BroadcastChannel(BROADCAST_CHANNEL)
  channel.onmessage = getChannelOnMessageCallback
}

globalThis.addEventListener('storage', (event: StorageEvent) => {
  if (event.key === WEBSOCKET_LEADER_KEY) checkLeader()
})
globalThis.addEventListener('beforeunload', () => {
  if (isLeader) {
    closeWebSocket(websocket)
    removeLeaderTabFromStorage()
    clearAllTimers(leaderInterval, leaderTimeout, retryTimeout)
    currentConnectionStatus = ConnectionStatus.NOT_STARTED
    isLeader = false
    hasRequestedState = false
  }
})

const handleDisconnection = () => {
  if (!isLeader) return

  if (retryCount < MAX_RETRIES) {
    retryCount++
    logger.info('[WebSocket] wss disconnected, will retry.')
    notifyConnectionStatus(ConnectionStatus.RETRYING)
    retryTimeout = getRetryTimeout(isLeader, connect)
  } else {
    logger.warn(
      '[WebSocket] Max connection retries reached. Will fallback to query polling.'
    )
    notifyConnectionStatus(ConnectionStatus.FAILED)
  }
}

const connect = () => {
  if (!isEnabled || !webSocketUrl || !jwtToken || !isLeader || websocket) return

  websocket = new WebSocket(webSocketUrl)

  websocket.onopen = () => {
    logger.info('[WebSocket] Connection opened, sending authorization token.')
    websocket?.send(
      JSON.stringify({ action: 'authorize_seller', token: jwtToken })
    )
    notifyConnectionStatus(ConnectionStatus.CONNECTED)
  }

  websocket.onmessage = (event: MessageEvent<string>) => {
    if (channel) broadcastOrders(channel, event.data, tabId)
    onMessageHandler(event.data)
  }

  websocket.onerror = () => {
    logger.warn('[WebSocket] WebSocket has thrown an error')
  }

  websocket.onclose = () => {
    handleDisconnection()
    websocket = null
  }
}

const becomeLeaderTab = () => {
  clearAllTimers(leaderInterval, leaderTimeout, retryTimeout)

  updateLeaderTabOnStorage(tabId)
  isLeader = true
  if (channel) {
    leaderInterval = getLeaderIsAliveInterval(channel, isLeader, tabId)
  }

  notifyConnectionStatus(ConnectionStatus.STARTING)
  retryCount = 0
  connect()
}

const removeOwnLeadership = () => {
  isLeader = false
  hasRequestedState = false
  closeWebSocket(websocket)
  clearAllTimers(leaderInterval, leaderTimeout, retryTimeout)
}

const startFollowerTimeout = () => {
  removeTimeout(leaderTimeout)
  leaderTimeout = getFollowerTabTimeout(becomeLeaderTab)
}

const becomeFollowerTab = () => {
  if (!hasRequestedState) {
    hasRequestedState = true
    if (channel) requestConnectionState(channel, tabId)
  }
  startFollowerTimeout()
}

const checkLeader = () => {
  if (!isEnabled) return

  const leaderTabId = getLeaderTabFromStorage()
  if (!leaderTabId) {
    becomeLeaderTab()
    return
  }

  const isStorageLeaderTabMe = leaderTabId === tabId
  if (isStorageLeaderTabMe) {
    const isAlreadyConnected = isLeader && !!websocket
    if (isAlreadyConnected) return
    becomeLeaderTab()
  } else {
    if (isLeader) removeOwnLeadership()
    becomeFollowerTab()
  }
}

export const initWebSocket = (
  isWebsocketEnabled: boolean,
  onConnectionStatusChangeCallback: ConnectionStatusCallback,
  onMessageHandlerCallback: MessageHandlerCallback
) => {
  setIsEnabled(isWebsocketEnabled)
  setConnectionStatusHandler(onConnectionStatusChangeCallback)
  setMessageHandler(onMessageHandlerCallback)
  initBroadcastChannel()
  checkLeader()
}
